import type { BuyerReservation, BuyerReservationCredit } from "./properties";
import { formatMinorCurrency } from "./currencyFormat";

// Pure presenter for a "My Reservations" dashboard row: status badge label +
// tone, the credit storyline for credited cancellations, and display dates.
// Kept out of Dashboard.tsx so every wording and visibility rule is unit
// tested without rendering the page.

export type ReservationBadgeTone = "green" | "blue" | "amber" | "grey";
export type CreditTone = "available" | "spent" | "revoked";

export interface ReservationDisplay {
  statusLabel: string;
  badgeTone: ReservationBadgeTone;
  creditLine: string | null;
  creditTone: CreditTone | null;
  spentOnLink: string | null;
  reservedAtLabel: string | null;
  cancelledAtLabel: string | null;
  // "Sold" pill next to the address — the property sold after this buyer
  // reserved it. Strict === true so an absent flag (older API) never tags.
  showSoldTag: boolean;
}

// en-GB short date ("1 May 2026") via Intl — no extra date dependency.
function formatDateLabel(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

// The one-line credit storyline on a credited cancellation. A null ledger
// status (row missing, or partner not ramped for redemption) must never read
// as spendable — it states only that a credit was issued.
function buildCreditLine(credit: BuyerReservationCredit | null): {
  creditLine: string | null;
  creditTone: CreditTone | null;
  spentOnLink: string | null;
} {
  if (!credit) return { creditLine: null, creditTone: null, spentOnLink: null };
  const amount =
    typeof credit.amount === "number" ? formatMinorCurrency(credit.amount, credit.currency) : null;
  if (credit.status === "available") {
    return { creditLine: `${amount ?? "Credit"} available`, creditTone: "available", spentOnLink: null };
  }
  if (credit.status === "spent") {
    const propertyName = credit.spentOn?.propertyName || "another property";
    // Only link when the spent-on property is still viewable — a hidden one
    // 404s on the listing page (deep-link visibility gate).
    const linkable = credit.spentOn?.viewable === true && !!credit.spentOn?.propertyId;
    return {
      creditLine: `Credit spent on ${propertyName}`,
      creditTone: "spent",
      spentOnLink: linkable ? `/listing/${credit.spentOn?.propertyId}` : null,
    };
  }
  if (credit.status === "revoked") {
    return { creditLine: "Credit revoked — refunded instead", creditTone: "revoked", spentOnLink: null };
  }
  return { creditLine: amount ? `${amount} credit issued` : "Credit issued", creditTone: "available", spentOnLink: null };
}

const STATUS_PRESENTATION: Record<
  BuyerReservation["status"],
  { label: string; tone: ReservationBadgeTone }
> = {
  reserved: { label: "Reserved", tone: "green" },
  cancelled_refunded: { label: "Cancelled — Refunded", tone: "blue" },
  cancelled_credited: { label: "Cancelled — Credit Issued", tone: "amber" },
  cancelled: { label: "Cancelled", tone: "grey" },
};

export function reservationDisplay(row: BuyerReservation): ReservationDisplay {
  const presentation = STATUS_PRESENTATION[row.status] ?? STATUS_PRESENTATION.cancelled;
  const statusLabel =
    row.status === "reserved" && row.paidWithCredit
      ? "Reserved — Paid with Credit"
      : presentation.label;
  const { creditLine, creditTone, spentOnLink } =
    row.status === "cancelled_credited"
      ? buildCreditLine(row.credit)
      : { creditLine: null, creditTone: null, spentOnLink: null };

  return {
    statusLabel,
    badgeTone: presentation.tone,
    creditLine,
    creditTone,
    spentOnLink,
    reservedAtLabel: formatDateLabel(row.reservedAt),
    cancelledAtLabel: formatDateLabel(row.cancelledAt),
    showSoldTag: row.property?.sold === true,
  };
}
